import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/auth';
import logo from '../../assets/logo.jpg';
import EmailIcon from '@mui/icons-material/Email';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublicShell from '../../components/PublicShell/PublicShell';
import './ConfirmEmail.css';

const ConfirmEmail = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener email desde el state de navegación o localStorage
  const email = location.state?.email || localStorage.getItem('confirmationEmail');

  useEffect(() => {
    // Si no hay email, redirigir al registro
    if (!email) {
      navigate('/register');
      return;
    }

    // Guardar email en localStorage para persistencia
    localStorage.setItem('confirmationEmail', email);
  }, [email, navigate]);

  useEffect(() => {
    // Manejar cooldown del reenvío
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!verificationCode.trim()) {
      setError('Por favor ingresa el código de verificación');
      return;
    }

    setLoading(true);

    try {
      await authService.confirmRegistration(email, verificationCode.trim());
      setSuccess('¡Email confirmado exitosamente!');
      
      // Limpiar datos temporales
      localStorage.removeItem('confirmationEmail');
      
      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Cuenta confirmada. Ya puedes iniciar sesión.',
            email: email 
          } 
        });
      }, 2000);
      
    } catch (err) {
      console.error('Error confirmando email:', err);
      
      let errorMessage = 'Error al confirmar el email';
      
      if (err.code === 'CodeMismatchException') {
        errorMessage = 'Código de verificación incorrecto';
      } else if (err.code === 'ExpiredCodeException') {
        errorMessage = 'El código ha expirado. Solicita uno nuevo';
      } else if (err.code === 'LimitExceededException') {
        errorMessage = 'Demasiados intentos. Espera antes de intentar nuevamente';
      } else if (err.code === 'UserNotFoundException') {
        errorMessage = 'Usuario no encontrado';
      } else if (err.code === 'NotAuthorizedException') {
        errorMessage = 'Usuario ya confirmado o código inválido';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setResendLoading(true);

    try {
      await authService.resendConfirmationCode(email);
      setSuccess('Código de verificación reenviado a tu email');
      setResendCooldown(60); // 60 segundos de cooldown
    } catch (err) {
      console.error('Error reenviando código:', err);
      
      let errorMessage = 'Error al reenviar el código';
      
      if (err.code === 'LimitExceededException') {
        errorMessage = 'Demasiados intentos. Espera antes de solicitar otro código';
      } else if (err.code === 'InvalidParameterException') {
        errorMessage = 'Usuario ya confirmado';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToRegister = () => {
    localStorage.removeItem('confirmationEmail');
    navigate('/register');
  };

  if (!email) {
    return null; // El useEffect redirigirá
  }

  return (
    <PublicShell>
      <div className="confirm-email-container">
      <div className="confirm-email-card">
        <div className="confirm-email-header">
          <div className="logo-container">
            <img src={logo} alt="Nissa Guard" className="logo-image" />
            <EmailIcon className="email-icon" />
          </div>
          <h1>Confirma tu Email</h1>
          <p>Hemos enviado un código de verificación a:</p>
          <div className="email-info">
            <span>{email}</span>
          </div>
          <p className="instruction">
            Ingresa el código de 6 dígitos que recibiste en tu email
          </p>
        </div>

        <form onSubmit={handleSubmit} className="confirm-email-form">
          <div className="form-group">
            <label htmlFor="verificationCode">Código de Verificación</label>
            <input
              type="text"
              id="verificationCode"
              value={verificationCode}
              onChange={(e) => {
                // Solo permitir números y limitar a 6 dígitos
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              placeholder="123456"
              maxLength="6"
              required
              disabled={loading}
              className="verification-input"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || verificationCode.length !== 6}
          >
            {loading ? 'Verificando...' : 'Confirmar Email'}
          </button>

          <div className="resend-section">
            <p>¿No recibiste el código?</p>
            <button
              type="button"
              className="btn-resend"
              onClick={handleResendCode}
              disabled={resendLoading || resendCooldown > 0}
            >
              <RefreshIcon className="resend-icon" />
              {resendLoading ? 'Reenviando...' : 
               resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 
               'Reenviar Código'}
            </button>
          </div>

          <div className="back-section">
            <button
              type="button"
              className="btn-back"
              onClick={handleBackToRegister}
              disabled={loading}
            >
              Volver al Registro
            </button>
          </div>
        </form>

        <div className="help-section">
          <h4>¿Problemas para recibir el código?</h4>
          <ul>
            <li>Revisa tu carpeta de spam o correo no deseado</li>
            <li>Verifica que el email sea correcto</li>
            <li>El código expira en 24 horas</li>
            <li>Puedes solicitar un nuevo código cada minuto</li>
          </ul>
        </div>
      </div>
      </div>
    </PublicShell>
  );
};

export default ConfirmEmail;
